import { z } from 'zod';
import { typedInputEncodingSchema } from '../conversations/typed-input';
import { agentDefinitionIdSchema, agentDefinitionSchema } from '../agent-ui';
import { apiEnvelope } from '../envelope';
import { notificationLaunchKindSchema } from '../notifications/schema';

/**
 * Server-owned agent definition handle for the returning-member base
 * conversation — the member's main, ongoing agent. Condition-agnostic: it names
 * the agent purpose, not a substance (ADR-015).
 */
export const BASE_AGENT_DEFINITION_ID = 'base-sona';

/**
 * Server-owned agent definition handle for first-call intake. Date-neutral so
 * re-cutting the underlying prompt (the ElevenLabs agent carries the dated
 * version) never churns this handle. Condition-agnostic: it names the agent
 * purpose, not a substance (ADR-015).
 */
export const INTAKE_SONA_V2_AGENT_DEFINITION_ID = 'intake-sona-v2';

/** Rollback definition for the previously shipped standard intake. */
export const INTAKE_SONA_V2_CONTROL_AGENT_DEFINITION_ID = 'intake-sona-v2-control';

/**
 * Server-owned agent definition handle for the CAB-onboarding intake agent.
 * Used when a member arrives via the sona-handoff deeplink and completes
 * the CAB onboarding flow. Condition-agnostic opaque handle (ADR-015, ADR-060).
 */
export const CAB_INTAKE_AGENT_DEFINITION_ID = 'cab-intake-sona';

/**
 * Server-owned agent definition handle for the CAB returning-member base
 * conversation — the follow-up to the CAB onboarding intake, where the member's
 * recent progress is reflected back to them. Condition-agnostic opaque handle
 * (ADR-015, ADR-060); the server resolves it for a member whose journey began in
 * the CAB onboarding flow, so the client never branches to select it.
 */
export const CAB_BASE_AGENT_DEFINITION_ID = 'cab-base-sona';

/**
 * Server-owned agent definition handle for the post-telemedicine follow-up
 * conversation, entered from the call-summary screen after a counsellor video
 * call. Condition-agnostic opaque handle (ADR-015, ADR-060).
 */
export const TELEMEDICINE_FOLLOWUP_AGENT_DEFINITION_ID = 'telemedicine-followup-sona';

/**
 * Returning-member orientation with optional, untrusted historical context.
 * Imported facts never override current member corrections or authorize writes.
 */
export const MIGRATION_SONA_AGENT_DEFINITION_ID = 'migration-sona';

/**
 * Server-owned agent definition handle for the journey-pathway Sona session
 * launched from a composed practice tile. The server resolves it from the
 * mint request's `composedPracticeId` — it is never client-selectable bare,
 * which is why it stays out of KNOWN_AGENT_DEFINITION_IDS. Condition-agnostic
 * opaque handle (ADR-015, ADR-060).
 */
export const JOURNEY_SONA_AGENT_DEFINITION_ID = 'journey-sona';

/**
 * Public, server-owned selector handles that a client can trust before the
 * voice-session endpoint resolves the request. Unknown selectors remain valid
 * request inputs so the server owns rejection, but must not enter pre-server
 * operational telemetry.
 *
 * Order is not semantic — the list is consumed as a Set by
 * isKnownAgentDefinitionId. The "intake-sona-v2 stays last" invariant belongs
 * to the agent-identity map (apps/api/src/config/agent-identity.ts), not to
 * this array.
 */
export const KNOWN_AGENT_DEFINITION_IDS = [
  BASE_AGENT_DEFINITION_ID,
  INTAKE_SONA_V2_AGENT_DEFINITION_ID,
  INTAKE_SONA_V2_CONTROL_AGENT_DEFINITION_ID,
  CAB_INTAKE_AGENT_DEFINITION_ID,
  CAB_BASE_AGENT_DEFINITION_ID,
  TELEMEDICINE_FOLLOWUP_AGENT_DEFINITION_ID,
  MIGRATION_SONA_AGENT_DEFINITION_ID,
] as const;

const knownAgentDefinitionIds: ReadonlySet<string> = new Set(KNOWN_AGENT_DEFINITION_IDS);

export function isKnownAgentDefinitionId(value: unknown): value is string {
  return typeof value === 'string' && knownAgentDefinitionIds.has(value);
}

/**
 * Request + response contract for POST /api/v1/voice/sessions.
 * Modelled on the eventual HTTP body shape, not the CLI-invoke wrapper — the
 * Lambda handler unwraps the wrapper and passes the body through this schema.
 */

export const clientPlatformSchema = z.enum(['ios', 'android', 'web']);
export type ClientPlatform = z.infer<typeof clientPlatformSchema>;

export const voiceStartupModeSchema = z.enum(['backend2_vpc', 'elevenlabs_saas']);
export type VoiceStartupMode = z.infer<typeof voiceStartupModeSchema>;

const voiceSessionRequestBaseSchema = z
  .object({
    /**
     * Canonical server-owned agent selector. Optional — when omitted the server
     * resolves its own default definition. The client never sends raw provider
     * agent ids for selection.
     */
    agentDefinitionId: agentDefinitionIdSchema.optional(),
    /**
     * Backward-compatible alias during migration. New code should use
     * agentDefinitionId.
     */
    agentFlowId: agentDefinitionIdSchema.optional(),
    /**
     * Reserved for future version pinning. MVP does NOT implement pinning, and
     * silently accepting the field would diverge schema from implementation.
     */
    agentFlowVersion: z
      .number()
      .int()
      .positive()
      .optional()
      .refine((v) => v === undefined, {
        message:
          'agentFlowVersion is reserved for GAL-296 and not yet supported; omit the field for MVP',
      }),
    /**
     * Defaults to "en" per ADR-017 when omitted. Accepted but currently ignored
     * by the MVP; localization is server-authored before mobile renders screens.
     */
    locale: z.string().min(2).optional(),
    /**
     * Observability + future platform-specific selection. Accepted but currently
     * ignored by the MVP.
     */
    clientPlatform: clientPlatformSchema.optional(),
    /**
     * A direct/QA selection that skips experiment assignment so the explicit
     * `agentDefinitionId` is honored exactly. Experiments (ADR-071) are for real
     * A/B enrollment; this is a dev/QA direct preview — member-authenticated, no
     * raw provider ids involved.
     */
    bypassExperiment: z.boolean().optional(),
    /**
     * Opaque client-generated key identifying one connect attempt. The client
     * mints a fresh UUID per user-initiated activation and re-sends the SAME
     * key on its bounded automatic retry, which is what scopes server-side
     * session reuse to genuine retries of the same attempt — a new attempt can
     * never be handed a prior attempt's room. Optional: requests without it
     * (older clients) are always served a fresh mint.
     */
    attemptKey: z.string().uuid().optional(),
    /** Native typed-input codec declared by this client; omission preserves literal text. */
    typedInputEncoding: typedInputEncodingSchema.optional(),
    /**
     * Opaque server-resolved selector for a journey practice launch (ADR-060
     * pattern): the id of the member's composed practice, carried from the
     * tile press. When present the server loads the row itself — ownership,
     * freshness, and the practice payload are all resolved server-side, so
     * practice content never transits the client.
     */
    composedPracticeId: z.string().uuid().optional(),
    /**
     * Opaque server-resolved selector for a notification launch (the ADR-060
     * pattern, same as `composedPracticeId` above): the id of the intent whose
     * push the member tapped, carried from the deeplink. The server loads the
     * row itself — ownership against the authenticated member, delivery status,
     * and freshness are all resolved server-side — so notification content
     * never transits the client in either direction.
     *
     * Unlike a practice launch, an unresolvable value here is NOT fatal: the
     * context it adds is an enrichment, so the mint proceeds without it
     * (ADR-088). That asymmetry is deliberate and lives in the resolver.
     *
     * Deliberately NOT `.uuid()`: the handle arrives from a deeplink, whose
     * param contract is a bare string, so a truncated or doctored link would
     * fail the whole `.strict()` request before the resolver ever ran — the
     * mint 400s, and a bounded client retry re-sends the same deterministic
     * 400. Shape is the resolver's business instead: it checks the id's form
     * itself and classifies a malformed one as `not_found` WITHOUT querying,
     * so a bad link never reaches the uuid column and is never mistaken for an
     * infrastructure failure. Bounded in length so an unbounded string never
     * reaches a query parameter.
     */
    notificationIntentId: z.string().min(1).max(200).optional(),
    /**
     * How the app was entered when the tap was captured — the one fact about a
     * notification launch the SERVER cannot derive, because it happens before
     * any request exists. Closed vocabulary, so a client that invents a value
     * fails the request rather than silently poisoning a chart dimension.
     * Meaningless without `notificationIntentId` and ignored without it.
     */
    notificationLaunchKind: notificationLaunchKindSchema.optional(),
    /**
     * The catalog element types this client bundle can render. The server
     * serves each fallback-bearing AgentScreen element only when its type is
     * declared here, and otherwise serves the element's authored fallback, so a
     * new element type never reaches a bundle whose strict host would reject
     * it. Absent means a client that predates the signal: every
     * fallback-bearing element is downgraded. Bounded so an oversized array
     * never reaches the downgrade; the server deduplicates.
     */
    renderCapabilities: z
      .array(z.string().regex(/^[A-Za-z0-9_]{1,64}$/))
      .max(256)
      .optional(),
  })
  .strict();

function assertCompatibleAgentSelectors(
  request: Pick<z.infer<typeof voiceSessionRequestBaseSchema>, 'agentDefinitionId' | 'agentFlowId'>,
  ctx: z.RefinementCtx,
): void {
  if (
    request.agentDefinitionId !== undefined &&
    request.agentFlowId !== undefined &&
    request.agentDefinitionId !== request.agentFlowId
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['agentDefinitionId'],
      message: 'agentDefinitionId and legacy agentFlowId must match when both are provided',
    });
  }
}

export const voiceSessionRequestSchema = voiceSessionRequestBaseSchema.superRefine(
  assertCompatibleAgentSelectors,
);

export type VoiceSessionRequest = z.infer<typeof voiceSessionRequestSchema>;

export function getVoiceSessionAgentDefinitionSelector(
  request: Pick<VoiceSessionRequest, 'agentDefinitionId' | 'agentFlowId'>,
): string | undefined {
  return request.agentDefinitionId ?? request.agentFlowId;
}

/**
 * The conversational model and reasoning effort the server asked the agent
 * worker to run for this session. Informational for clients (the internal
 * Sona debug trail shows it); the worker remains free to fall back to its
 * baked model, so this is what was requested, not proof of what ran.
 */
export const voiceSessionAgentLlmSchema = z.object({
  model: z.string().min(1),
  reasoningEffort: z.string().min(1).optional(),
});
export type VoiceSessionAgentLlm = z.infer<typeof voiceSessionAgentLlmSchema>;

/**
 * Success payload. `agentConfig` is the server's client-safe projection of the
 * agent config (today only the provider `agentId`); worker-only fields such as
 * the post-call webhook URL ride the agent dispatch and never this response.
 * It is an open record, but a new client-visible field is still a wire change
 * that needs the same compatibility review as any other response field.
 * `agentDefinition` is the product contract mobile uses to render and enforce
 * the selected voice/chat agent UI.
 */
export const voiceSessionDataSchema = z.object({
  /** Present only when the server durably recorded this native input codec. */
  typedInputEncoding: typedInputEncodingSchema.optional(),
  livekitUrl: z.string().url(),
  roomName: z.string().min(1),
  /**
   * The minted per-session id (the voice_sessions binding). Clients carry it
   * into post-session artifacts (e.g. feedback submissions) so server-side
   * consumers can join back to the session's transcript, and emit it on voice
   * telemetry — it is the same id the worker logs on every Datadog log line,
   * enabling mobile telemetry ↔ worker log correlation.
   */
  sessionId: z.string().uuid(),
  participantToken: z.string().min(1),
  agentConfig: z.record(z.string(), z.unknown()),
  agentDefinition: agentDefinitionSchema,
  /**
   * The voice id actually dispatched to the worker for this session — the
   * post-fallback truth (stored preference, else the agent's metadata voice,
   * else the catalog default), so clients and QA can observe what was applied.
   */
  activeVoiceId: z.string().min(1),
  /**
   * True when the server re-issued credentials for an existing still-serviceable
   * session (same member + agent definition) instead of minting a new room and
   * dispatching another worker. Absent on fresh mints — additive, so older
   * clients parse both shapes unchanged.
   */
  reused: z.boolean().optional(),
  /**
   * The per-call model override sent on the worker dispatch. Present exactly
   * when the dispatch carried one; absent for definitions that run the
   * worker's baked model, for a kill-switched mint, and for reused sessions.
   */
  agentLlm: voiceSessionAgentLlmSchema.optional(),
});

export type VoiceSessionData = z.infer<typeof voiceSessionDataSchema>;

/** ADR-002 envelope: `{ data, error }`, error is the structured `apiErrorSchema` ({ code, message, details? }). */
export const voiceSessionResponseSchema = apiEnvelope(voiceSessionDataSchema);
export type VoiceSessionResponse = z.infer<typeof voiceSessionResponseSchema>;

/**
 * Request + response contract for POST /api/v1/voice/elevenlabs/credentials.
 * This is the direct ElevenLabs SaaS WebRTC startup mode: the server resolves
 * the agent from a flow id, mints a temporary conversation token with the
 * server-side API key, and returns the SDK credential plus the fully rendered
 * per-call overrides used by the VPC dispatch path. It accepts the same startup
 * selector fields as POST /api/v1/voice/sessions so the client can use one
 * provider interface for both integrations.
 */
export const elevenLabsSaasCredentialRequestSchema = voiceSessionRequestBaseSchema
  .pick({
    agentDefinitionId: true,
    agentFlowId: true,
    agentFlowVersion: true,
    locale: true,
    clientPlatform: true,
    bypassExperiment: true,
    // Accepted for client-request-shape parity with the sessions route. The
    // SaaS path mints no LiveKit room, so there is nothing to reuse, but it
    // still stores the key on the session row, where a caller can find the
    // session it minted.
    attemptKey: true,
    // Also accepted for parity, but NOT supported: one client request object
    // is posted to whichever transport is selected, so omitting the key here
    // would reject a journey launch with an opaque "unrecognized key" instead
    // of the uniform launch-failure envelope. The SaaS path validates it and
    // fails the launch closed — see `rejectUnsupportedPracticeTransport`.
    composedPracticeId: true,
    // Accepted for the same reason as `composedPracticeId` above: one client
    // request object is posted to whichever transport is selected, so omitting
    // the key here would reject the WHOLE mint with an opaque "unrecognized
    // key" error rather than degrading the enrichment. Unlike the practice
    // case, the SaaS path can serve this one — the block is fail-open, so it
    // resolves normally here instead of failing the launch closed.
    notificationIntentId: true,
    notificationLaunchKind: true,
    // Served here too: this route returns the same agentDefinition.
    renderCapabilities: true,
  })
  .superRefine(assertCompatibleAgentSelectors);

export type ElevenLabsSaasCredentialRequest = z.infer<typeof elevenLabsSaasCredentialRequestSchema>;

export const saasConversationOverridesSchema = z
  .object({
    systemPromptOverride: z.string().min(1),
    firstMessage: z.string().min(1).optional(),
    voiceId: z.string().min(1),
    voiceSettings: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export type SaasConversationOverrides = z.infer<typeof saasConversationOverridesSchema>;

export const elevenLabsSaasCredentialDataSchema = z
  .object({
    conversationToken: z.string().min(1),
    /**
     * Mint-time session id (ADR-045 Rule 6 option-(b)): the client passes it
     * to the provider as the `sessionId` dynamic variable at conversation
     * start, so a server tool can resolve the member from the session row
     * this server wrote BEFORE the call — the only binding that can succeed
     * mid-call (the provider conversation id is backfilled post-call).
     */
    sessionId: z.string().uuid(),
    overrides: saasConversationOverridesSchema,
    agentDefinition: agentDefinitionSchema,
  })
  .strict();

export type ElevenLabsSaasCredentialData = z.infer<typeof elevenLabsSaasCredentialDataSchema>;

export const elevenLabsSaasCredentialResponseSchema = apiEnvelope(
  elevenLabsSaasCredentialDataSchema,
);

export type ElevenLabsSaasCredentialResponse = z.infer<
  typeof elevenLabsSaasCredentialResponseSchema
>;

// ─── prompt audit (voice_session_prompt_audits) ─────────────────────────────

/**
 * Memory-injection outcome recorded on the per-mint prompt audit row.
 * `present`/`empty` mirror the mint seam's MemoryBlockResult outcomes;
 * `absent` records that the agent's prompt declares no memory slot, so the
 * seam was never invoked.
 */
export const PROMPT_AUDIT_MEMORY_OUTCOME = ['present', 'empty', 'absent'] as const;
export type PromptAuditMemoryOutcome = (typeof PROMPT_AUDIT_MEMORY_OUTCOME)[number];

/**
 * Degradation reason when the seam replaced a real memory block with the
 * canonical empty state (mirrors MemoryBlockResult.failure).
 */
export const PROMPT_AUDIT_MEMORY_FAILURE = ['error', 'oversize', 'timeout'] as const;
export type PromptAuditMemoryFailure = (typeof PROMPT_AUDIT_MEMORY_FAILURE)[number];

/**
 * Previous-conversations feed outcome recorded on the per-mint prompt audit
 * row. `present`/`empty` mirror the loader's result outcomes; `absent` records
 * that the agent's prompt declares no previous-conversations slot; `disabled`
 * records that the operational kill switch was off — distinguishable from
 * `absent` in one query so "no slot" and "switch off" never conflate.
 */
export const PROMPT_AUDIT_PREVIOUS_CONVERSATIONS_OUTCOME = [
  'present',
  'empty',
  'absent',
  'disabled',
] as const;
export type PromptAuditPreviousConversationsOutcome =
  (typeof PROMPT_AUDIT_PREVIOUS_CONVERSATIONS_OUTCOME)[number];

/**
 * Degradation reason when the previous-conversations loader replaced a real
 * block with the canonical empty state (mirrors the loader result's failure).
 */
export const PROMPT_AUDIT_PREVIOUS_CONVERSATIONS_FAILURE = [
  'error',
  'timeout',
  'oversize',
] as const;
export type PromptAuditPreviousConversationsFailure =
  (typeof PROMPT_AUDIT_PREVIOUS_CONVERSATIONS_FAILURE)[number];

/**
 * Migration-context feed outcome recorded on the per-mint prompt audit row.
 * `present`/`empty` mirror the loader's result outcomes; `absent` records that
 * the agent's prompt declares no migration-context slot; `disabled` records
 * that the operational kill switch was off — distinguishable from `absent` in
 * one query so "no slot" and "switch off" never conflate.
 */
export const PROMPT_AUDIT_MIGRATION_CONTEXT_OUTCOME = [
  'present',
  'empty',
  'absent',
  'disabled',
] as const;
export type PromptAuditMigrationContextOutcome =
  (typeof PROMPT_AUDIT_MIGRATION_CONTEXT_OUTCOME)[number];

/**
 * Degradation reason when the migration-context loader replaced a real block
 * with the canonical empty state (mirrors the loader result's failure).
 */
export const PROMPT_AUDIT_MIGRATION_CONTEXT_FAILURE = ['error', 'timeout', 'oversize'] as const;
export type PromptAuditMigrationContextFailure =
  (typeof PROMPT_AUDIT_MIGRATION_CONTEXT_FAILURE)[number];

/**
 * Why the member's app is reporting a voice session ended. `connect_failed`
 * means the session was minted but the client never established (or gave up
 * establishing) the call, so the server may release the room it created.
 */
export const voiceSessionEndReasonSchema = z.enum(['completed', 'connect_failed']);
export type VoiceSessionEndReason = z.infer<typeof voiceSessionEndReasonSchema>;

/**
 * Closed classification of a client voice connect failure. The first seven
 * values mirror livekit-client's `ConnectionErrorReason` (0–6, in order); the
 * rest are client-side classes for failures that never reach LiveKit or that
 * the client's own watchdogs end. Never carries provider message text.
 */
export const VOICE_CONNECT_FAILURE_REASONS = [
  'not_allowed',
  'server_unreachable',
  'internal_error',
  'cancelled',
  'leave_request',
  'timeout',
  'websocket',
  'audio_session',
  'startup_timeout',
  'greeting_timeout',
  'unknown',
] as const;
export const voiceConnectFailureReasonSchema = z.enum(VOICE_CONNECT_FAILURE_REASONS);
export type VoiceConnectFailureReason = z.infer<typeof voiceConnectFailureReasonSchema>;

/**
 * livekit-client's `DisconnectReason` protocol enum as snake_case names, in
 * numeric order (index = protocol value). A leave request or server-side
 * disconnect carries one of these; the name, never a message, is what is
 * reported.
 */
export const VOICE_DISCONNECT_REASONS = [
  'unknown_reason',
  'client_initiated',
  'duplicate_identity',
  'server_shutdown',
  'participant_removed',
  'room_deleted',
  'state_mismatch',
  'join_failure',
  'migration',
  'signal_close',
  'room_closed',
  'user_unavailable',
  'user_rejected',
  'sip_trunk_failure',
  'connection_timeout',
  'media_failure',
  'agent_error',
] as const;
export const voiceDisconnectReasonSchema = z.enum(VOICE_DISCONNECT_REASONS);
export type VoiceDisconnectReason = z.infer<typeof voiceDisconnectReasonSchema>;
